/**
 * Sentry init for the admin + customer SPAs (same codebase, different
 * REACT_APP_CONSOLE build args). The console name is added as a Sentry
 * tag so you can filter errors by surface: `console:admin` vs
 * `console:recruiter`.
 *
 * Inert when REACT_APP_SENTRY_DSN is unset.
 */
import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENV = process.env.REACT_APP_SENTRY_ENV || "production";
const CONSOLE_NAME = (process.env.REACT_APP_CONSOLE || "admin").toLowerCase();
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || `aieta-${CONSOLE_NAME}`;

let initialised = false;

export function initSentry() {
  if (initialised || !DSN) return;
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,

    // Higher trace sample rate on the admin SPAs vs the public portal —
    // these are lower-traffic + higher-value flows where one slow query
    // (BU P&L, MIS aggregation) is worth seeing on every hit.
    tracesSampleRate: ENV === "production" ? 0.25 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.5,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Sensitive: candidate names, salaries, interview transcripts in the
        // UI. Mask everything by default; the replay still shows clicks +
        // navigation but no readable text.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "ChunkLoadError",
      "AbortError",                       // benign axios aborts on route change
    ],

    beforeSend(event) {
      try {
        if (event.breadcrumbs) {
          for (const b of event.breadcrumbs) {
            if (b?.data?.request?.headers?.Authorization) {
              b.data.request.headers.Authorization = "[redacted]";
            }
          }
        }
      } catch { /* noop */ }
      return event;
    },
  });
  Sentry.setTag("console", CONSOLE_NAME);
  initialised = true;
}

export function setSentryUser(user) {
  if (!DSN) return;
  if (!user) { Sentry.setUser(null); return; }
  Sentry.setUser({
    id: user.user_id,
    email: user.email,
    role: user.role,
    org_id: user.organization_id,
  });
  Sentry.setTag("role", user.role || "anonymous");
  if (user.organization_id) Sentry.setTag("org_id", user.organization_id);
}

export { Sentry };
